import React, { useState, useEffect } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

export default function ContasReceberDropdown() {
  console.log('🔧 ContasReceberDropdown renderizando...');
  
  const [contasReceber, setContasReceber] = useState([]);
  const [contasReceberFiltradas, setContasReceberFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Estados para contas específicas por tipo
  const [contasDeServicos, setContasDeServicos] = useState([]);
  const [contasDeProdutos, setContasDeProdutos] = useState([]);
  const [contasDeProdutosFiltradas, setContasDeProdutosFiltradas] = useState([]);
  const [loadingContas, setLoadingContas] = useState(false);
  const [errorContas, setErrorContas] = useState('');

  // Estados para filtro
  const [filtro, setFiltro] = useState('cliente');
  const [valorFiltro, setValorFiltro] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);

  // Estados para abas
  const [abaAtiva, setAbaAtiva] = useState('servicos'); // 'servicos' ou 'produtos'

  // Estados para popup
  const [popupAberto, setPopupAberto] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);

  // Estados para configuração de pagamento no popup
  const [tiposPagamento, setTiposPagamento] = useState([]);
  const [configuracoesPagamento, setConfiguracoesPagamento] = useState([]);
  const [configuracoesFiltradas, setConfiguracoesFiltradas] = useState([]);
  const [tipoPagamentoSelecionado, setTipoPagamentoSelecionado] = useState(null);
  const [configuracaoSelecionadaId, setConfiguracaoSelecionadaId] = useState(null);
  const [quantidadeParcelas, setQuantidadeParcelas] = useState(1);
  const [dataInicioVencimento, setDataInicioVencimento] = useState(() => new Date().toISOString().split('T')[0]);
  const [calculoPagamento, setCalculoPagamento] = useState(null);
  const [carregandoCalculo, setCarregandoCalculo] = useState(false);
  const [erroCalculo, setErroCalculo] = useState('');
  const [tipoJurosUI, setTipoJurosUI] = useState('');
  
  // Estado para armazenar informações detalhadas das vendas (tipo de juros, etc.)
  const [dadosVendas, setDadosVendas] = useState({});

  // URLs para fallback da API (usando as mesmas dos outros dropdowns funcionais)
  const baseUrls = [
    BASE_URL,
    BASE_URL,
    'https://localhost:44371/api',
    'http://localhost:64918/api',
    'http://localhost:5000/api'
  ];

  // Função para fazer chamadas API com fallback
  const apiCall = async (endpoint, method = 'GET', body = null) => {
    for (let baseUrl of baseUrls) {
      try {
        const url = `${baseUrl}/${endpoint}`;
        console.log(`🌐 Tentando: ${url}`);
        
        const config = {
          method,
          headers: { 'Content-Type': 'application/json' },
        };
        const token = getAuthToken();
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        
        if (body && method !== 'GET') {
          config.body = JSON.stringify(body);
        }
        
        const response = await fetch(url, config);
        
        if (response.ok) {
          console.log(`✅ SUCESSO em: ${url} - Status: ${response.status}`);
          return await response.json();
        }
        console.log(`❌ Falha em: ${url} - Status: ${response.status} - ${response.statusText}`);
      } catch (error) {
        console.log(`❌ Erro em: ${baseUrl}/${endpoint} - ${error.message}`);
      }
    }
    throw new Error(`Falha em todas as tentativas para ${endpoint}`);
  };

  // Carregar contas a receber
  const fetchContasReceber = async () => {
    try {
      setLoading(true);
      console.log('📊 Carregando contas a receber...');
      
      const data = await apiCall('ContasReceber');
      console.log('💰 Contas a receber carregadas:', data);
      
      setContasReceber(Array.isArray(data) ? data : []);
      setContasReceberFiltradas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Erro ao buscar contas a receber:', error);
      
      // Diagnóstico mais específico do erro
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setError('⚠️ Backend não está respondindo. Verifique se o servidor está rodando na porta 7040.');
      } else if (error.message.includes('404')) {
        setError('⚠️ Endpoint api/ContasReceber não encontrado. Verifique se o controller está ativo.');
      } else {
        setError(`⚠️ Erro: ${error.message}`);
      }
      
      setContasReceber([]);
      setContasReceberFiltradas([]);
    } finally {
      setLoading(false);
    }
  };

  // Carregar tipos de pagamento
  const fetchTiposPagamento = async () => {
    try {
      const data = await apiCall('TipoPagamento');
      console.log('💳 Tipos de pagamento carregados:', data);
      setTiposPagamento(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('❌ Erro ao carregar tipos de pagamento:', e);
      setTiposPagamento([]);
    }
  };

  useEffect(() => {
    fetchContasReceber();
    fetchContasReceberPorTipo(); // Nova função para carregar contas por tipo
    fetchTiposPagamento();
    fetchConfiguracoesPagamento();
  }, []);

  useEffect(() => {
    const handler = () => {
      setPopupAberto(false);
      setContaSelecionada(null);
      setTipoPagamentoSelecionado(null);
      setConfiguracaoSelecionadaId(null);
      setQuantidadeParcelas(1);
      setDataInicioVencimento(new Date().toISOString().split('T')[0]);
      setCalculoPagamento(null);
      setErroCalculo('');
    };
    window.addEventListener('contasreceber-fechar-todos-popups', handler);
    return () => window.removeEventListener('contasreceber-fechar-todos-popups', handler);
  }, []);

  // Função para buscar todas as contas a receber e separá-las por tipo
  const fetchContasReceberPorTipo = async () => {
    setLoadingContas(true);
    setErrorContas('');
    
    try {
      console.log('📥 Buscando contas a receber por tipo...');
      const data = await apiCall('ContasReceber');
      console.log('✅ Contas a receber carregadas:', data);
      
      const contasArray = Array.isArray(data) ? data : [];
      
      // Separar contas de serviços (têm OrcamentoId) e produtos (não têm OrcamentoId ou VendaId direta)
      const contasDeServicos = contasArray.filter(conta => {
        const orcamentoId = conta.orcamentoId || conta.OrcamentoId;
        return orcamentoId !== null && orcamentoId !== undefined && orcamentoId !== 0;
      });
      
      const contasDeProdutos = contasArray.filter(conta => {
        const orcamentoId = conta.orcamentoId || conta.OrcamentoId;
        const vendaId = conta.vendaId || conta.VendaId;
        // Produtos: têm VendaId mas não têm OrcamentoId (vendas diretas de produto)
        return (orcamentoId === null || orcamentoId === undefined || orcamentoId === 0) && 
               (vendaId !== null && vendaId !== undefined && vendaId !== 0);
      });
      
      setContasDeServicos(contasDeServicos);
      setContasDeProdutos(contasDeProdutos);
      
      // Inicializar listas filtradas
      setContasReceberFiltradas(contasDeServicos);
      setContasDeProdutosFiltradas(contasDeProdutos);
      
      console.log(`🔍 Separação por tipo: ${contasDeServicos.length} serviços, ${contasDeProdutos.length} produtos`);
      
      // Carregar dados detalhados das vendas para ambos os tipos
      await carregarDadosVendas([...contasDeServicos, ...contasDeProdutos]);
      
    } catch (error) {
      console.error('❌ Erro ao buscar contas por tipo:', error);
      
      // Diagnóstico mais específico do erro
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setErrorContas('⚠️ Backend não está respondendo. Verifique se o servidor está rodando na porta 7040.');
      } else if (error.message.includes('404')) {
        setErrorContas('⚠️ Endpoint api/ContasReceber não encontrado. Verifique se o controller está ativo.');
      } else {
        setErrorContas(`⚠️ Erro: ${error.message}`);
      }
      
      setContasDeServicos([]);
      setContasDeProdutos([]);
      setContasReceberFiltradas([]);
      setContasDeProdutosFiltradas([]);
    } finally {
      setLoadingContas(false);
    }
  };
  
  // Função para carregar dados detalhados das vendas (incluindo tipo de juros)
  const carregarDadosVendas = async (contas) => {
    const dadosVendasTemp = {};
    
    // Processar contas em lotes para evitar sobrecarga
    const contasComVenda = contas.filter(conta => {
      const vendaId = conta.vendaId || conta.VendaId;
      return vendaId && vendaId !== 0;
    });
    
    console.log(`🔍 Carregando dados para ${contasComVenda.length} vendas...`);
    
    for (const conta of contasComVenda) {
      const vendaId = conta.vendaId || conta.VendaId;
      
      try {
        const vendaData = await apiCall(`Vendas/${vendaId}`);
        
        if (vendaData) {
          // Buscar configuração de pagamento para obter tipo de juros
          const configId = vendaData.configuracaoPagamentoId || vendaData.ConfiguracaoPagamentoId;
          let tipoJuros = 'N/A';
          let configuracaoDescricao = 'N/A';
          
          if (configId) {
            try {
              const configData = await apiCall(`ConfiguracaoPagamento/${configId}`);
              if (configData) {
                const tipoJurosRaw = configData.tipoJuros || configData.TipoJuros || '';
                const valorJuros = configData.valorJuros || configData.ValorJuros || 0;
                configuracaoDescricao = configData.descricaoCompleta || configData.DescricaoCompleta || 'N/A';
                
                // Definir tipo de juros baseado nos dados
                if (valorJuros === 0 || configuracaoDescricao.toUpperCase().includes('SEM')) {
                  tipoJuros = 'Sem Juros';
                } else if (tipoJurosRaw.toUpperCase() === 'PERCENTUAL') {
                  tipoJuros = `${valorJuros}%`;
                } else if (tipoJurosRaw.toUpperCase() === 'ABSOLUTO') {
                  tipoJuros = `R$ ${valorJuros.toFixed(2)}`;
                } else {
                  tipoJuros = tipoJurosRaw || 'N/A';
                }
              }
            } catch (configError) {
              console.warn(`⚠️ Erro ao buscar configuração ${configId}:`, configError);
            }
          }
          
          dadosVendasTemp[vendaId] = {
            tipoJuros,
            configuracaoDescricao,
            valorJuros: vendaData.valorJuros || vendaData.ValorJuros || 0,
            valorOriginal: vendaData.valorOriginal || vendaData.ValorOriginal || 0,
            valorFinal: vendaData.valorFinal || vendaData.ValorFinal || 0,
            quantidadeParcelas: vendaData.quantidadeParcelas || vendaData.QuantidadeParcelas || 1
          };
        }
      } catch (error) {
        console.warn(`⚠️ Erro ao buscar dados da venda ${vendaId}:`, error);
        dadosVendasTemp[vendaId] = {
          tipoJuros: 'N/A',
          configuracaoDescricao: 'N/A',
          valorJuros: 0,
          valorOriginal: 0,
          valorFinal: 0,
          quantidadeParcelas: 1
        };
      }
    }
    
    setDadosVendas(dadosVendasTemp);
    console.log('✅ Dados das vendas carregados:', dadosVendasTemp);
  };

  // useEffect para filtrar configurações por tipo de pagamento
  useEffect(() => {
    filtrarConfiguracoesPorTipo(tipoPagamentoSelecionado);
  }, [tipoPagamentoSelecionado, configuracoesPagamento]);

  // useEffect para atualizar UI de juros
  useEffect(() => {
    if (configuracaoSelecionadaId) {
      const cfg = configuracoesPagamento.find(c => (c.id || c.Id) === configuracaoSelecionadaId);
      if (cfg) {
        const tipoJuros = cfg.tipoJuros || cfg.TipoJuros || '';
        const valor = cfg.valorJuros || cfg.ValorJuros || 0;
        const desc = cfg.configuracaoJurosDescricao || cfg.ConfiguracaoJurosDescricao || '';
        
        // Definir valores que correspondem às opções do select
        if (desc.toUpperCase().includes('SEM') || valor === 0) {
          setTipoJurosUI('SEM_JUROS');
        } else if (tipoJuros.toUpperCase() === 'PERCENTUAL') {
          setTipoJurosUI('PERCENTUAL');
        } else if (tipoJuros.toUpperCase() === 'ABSOLUTO') {
          setTipoJurosUI('ABSOLUTO');
        } else {
          setTipoJurosUI('');
        }
        
        console.log('🔧 Tipo de juros definido automaticamente:', {
          tipoJuros,
          valor,
          desc,
          tipoJurosUIDefinido: tipoJuros.toUpperCase() === 'PERCENTUAL' ? 'PERCENTUAL' : 
                              tipoJuros.toUpperCase() === 'ABSOLUTO' ? 'ABSOLUTO' : 
                              (desc.toUpperCase().includes('SEM') || valor === 0) ? 'SEM_JUROS' : ''
        });
      }
    } else {
      setTipoJurosUI('');
    }
  }, [configuracaoSelecionadaId, configuracoesPagamento]);

  // useEffect para executar cálculo automaticamente sempre que houver mudanças
  useEffect(() => {
    if (configuracaoSelecionadaId && contaSelecionada) {
      console.log('🔄 Executando cálculo automático devido a mudança...');
      const timer = setTimeout(async () => {
        // Buscar dados da venda para obter valor original correto
        const vendaId = contaSelecionada.vendaId || contaSelecionada.VendaId;
        if (vendaId) {
          try {
            const vendaData = await apiCall(`Vendas/${vendaId}`);
            const valorOrc = vendaData.valorOrc || vendaData.ValorOrc || 0;
            const desconto = vendaData.desconto || vendaData.Desconto || 0;
            const valorOriginalBase = valorOrc - desconto;
            
            console.log('� Recalculando com valor original:', valorOriginalBase);
            if (valorOriginalBase > 0) {
              executarCalculoPagamento(valorOriginalBase);
            }
          } catch (error) {
            console.error('❌ Erro ao buscar dados da venda para recálculo:', error);
            // Em caso de erro, usar o valor total como fallback (não ideal, mas mantém funcionamento)
            if (contaSelecionada.valorTotal) {
              executarCalculoPagamento(contaSelecionada.valorTotal);
            }
          }
        } else if (contaSelecionada.valorTotal) {
          // Se não há vendaId, usar valor total como fallback
          executarCalculoPagamento(contaSelecionada.valorTotal);
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [configuracaoSelecionadaId, quantidadeParcelas, dataInicioVencimento, tipoJurosUI, contaSelecionada]);

  // useEffect específico para executar cálculo automaticamente quando popup abre com dados preenchidos
  useEffect(() => {
    // Verificar se popup está aberto e tem dados completos para calcular
    if (popupAberto && configuracaoSelecionadaId && contaSelecionada && quantidadeParcelas && dataInicioVencimento) {
      console.log('🚀 Popup aberto com configuração preenchida, executando cálculo automático...');
      
      const timer = setTimeout(async () => {
        const vendaId = contaSelecionada.vendaId || contaSelecionada.VendaId;
        if (vendaId) {
          try {
            const vendaData = await apiCall(`Vendas/${vendaId}`);
            const valorOrc = vendaData.valorOrc || vendaData.ValorOrc || 0;
            const desconto = vendaData.desconto || vendaData.Desconto || 0;
            const valorOriginalBase = valorOrc - desconto;
            
            console.log('🔄 Executando cálculo automático inicial do popup com valor:', valorOriginalBase);
            if (valorOriginalBase > 0) {
              executarCalculoPagamento(valorOriginalBase);
            }
          } catch (error) {
            console.error('❌ Erro ao buscar dados da venda para cálculo inicial:', error);
            if (contaSelecionada.valorTotal) {
              executarCalculoPagamento(contaSelecionada.valorTotal);
            }
          }
        } else if (contaSelecionada.valorTotal) {
          executarCalculoPagamento(contaSelecionada.valorTotal);
        }
      }, 100); // Timeout menor para execução mais rápida
      
      return () => clearTimeout(timer);
    }
  }, [popupAberto, configuracaoSelecionadaId, contaSelecionada, quantidadeParcelas, dataInicioVencimento]);

  // Filtrar contas a receber
  useEffect(() => {
    if (!valorFiltro.trim()) {
      setContasReceberFiltradas(contasReceber);
      return;
    }

    const filtradas = contasReceber.filter(conta => {
      const valorBusca = valorFiltro.toLowerCase();
      
      switch (filtro) {
        case 'cliente':
          return conta.clienteNome?.toLowerCase().includes(valorBusca) || 
                 conta.clienteId?.toString().includes(valorBusca);
        case 'valor':
          return conta.valorTotal?.toString().includes(valorBusca);
        case 'vencimento':
          return conta.dataVencimento?.includes(valorBusca);
        case 'status':
          return conta.statusPagamento?.toLowerCase().includes(valorBusca);
        default:
          return true;
      }
    });
    
    setContasReceberFiltradas(filtradas);
  }, [contasReceber, filtro, valorFiltro]);

  // Filtrar contas de produtos
  useEffect(() => {
    if (!valorFiltro.trim()) {
      setContasDeProdutosFiltradas(contasDeProdutos);
      return;
    }

    const filtradas = contasDeProdutos.filter(conta => {
      const valorBusca = valorFiltro.toLowerCase();
      
      switch (filtro) {
        case 'cliente':
          return conta.clienteNome?.toLowerCase().includes(valorBusca) || 
                 conta.clienteId?.toString().includes(valorBusca);
        case 'valor':
          return conta.valorTotal?.toString().includes(valorBusca);
        case 'vencimento':
          return conta.dataVencimento?.includes(valorBusca);
        case 'status':
          return conta.statusPagamento?.toLowerCase().includes(valorBusca);
        default:
          return true;
      }
    });
    
    setContasDeProdutosFiltradas(filtradas);
  }, [contasDeProdutos, filtro, valorFiltro]);

  const opcoesFiltro = [
    { valor: 'cliente', label: 'Cliente' },
    { valor: 'valor', label: 'Valor' },
    { valor: 'vencimento', label: 'Data Vencimento' },
    { valor: 'status', label: 'Status' }
  ];

  const limparFiltro = () => {
    setValorFiltro('');
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(valor || 0);
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      return new Date(data).toLocaleDateString('pt-BR');
    } catch {
      return data;
    }
  };
  
  // Função para obter informações de tipo de juros para uma conta
  const obterInfoJuros = (conta) => {
    const vendaId = conta.vendaId || conta.VendaId;
    const dadosVenda = dadosVendas[vendaId];
    
    if (dadosVenda && dadosVenda.tipoJuros !== 'N/A') {
      return {
        tipo: dadosVenda.tipoJuros,
        valor: dadosVenda.valorJuros,
        configuracao: dadosVenda.configuracaoDescricao
      };
    }
    
    // Fallback para dados básicos da conta
    const juros = conta.juros || 0;
    if (juros > 0) {
      return {
        tipo: 'Calculado',
        valor: juros,
        configuracao: 'Configuração aplicada'
      };
    }
    
    return {
      tipo: 'Sem Juros',
      valor: 0,
      configuracao: 'Sem configuração'
    };
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pago': return '#4caf50';
      case 'pendente': return 'rgb(235, 133, 0)';
      case 'vencido': return '#f44336';
      default: return '#666';
    }
  };

  // Função para abrir popup com detalhes da conta e carregar dados da venda
  const abrirPopupConta = async (conta) => {
    setContaSelecionada(conta);
    setPopupAberto(true);
    
    // Se a conta tem vendaId, buscar os dados da venda para preencher automaticamente
    const vendaId = conta.vendaId || conta.VendaId;
    if (vendaId) {
      try {
        console.log(`🔍 Buscando dados da venda ${vendaId} para preencher configuração...`);
        const vendaData = await apiCall(`Vendas/${vendaId}`);
        console.log('📋 Dados da venda carregados:', vendaData);
        
        // Preencher automaticamente os campos da configuração de pagamento
        if (vendaData.tipoPagamentoId || vendaData.TipoPagamentoId) {
          const tipoId = vendaData.tipoPagamentoId || vendaData.TipoPagamentoId;
          setTipoPagamentoSelecionado(tipoId);
          
          // Aguardar configurações carregarem e então processar
          setTimeout(async () => {
            // Garantir que configurações estão carregadas
            if (configuracoesPagamento.length === 0) {
              console.log('⏳ Recarregando configurações de pagamento...');
              await fetchConfiguracoesPagamento();
            }
            
            // Filtrar configurações para o tipo de pagamento
            const configsFiltradas = configuracoesPagamento.filter(cfg => 
              (cfg.tipoPagamentoId || cfg.TipoPagamentoId) === tipoId
            );
            console.log('🔍 Configurações filtradas:', configsFiltradas.length);
            setConfiguracoesFiltradas(configsFiltradas);
            
            // Preencher configuração específica se existir
            if (vendaData.configuracaoPagamentoId || vendaData.ConfiguracaoPagamentoId) {
              const configId = vendaData.configuracaoPagamentoId || vendaData.ConfiguracaoPagamentoId;
              console.log('🧩 Definindo configuração ID:', configId);
              setConfiguracaoSelecionadaId(configId);
              
              // Buscar configuração específica para definir tipo de juros
              const cfg = configuracoesPagamento.find(c => (c.id || c.Id) === configId);
              if (cfg) {
                const tipoJuros = cfg.tipoJuros || cfg.TipoJuros || '';
                const valorJuros = cfg.valorJuros || cfg.ValorJuros || 0;
                const descricaoJuros = cfg.configuracaoJurosDescricao || cfg.ConfiguracaoJurosDescricao || '';
                
                console.log('🧩 Configuração encontrada:', { 
                  id: cfg.id || cfg.Id,
                  tipoJuros, 
                  valorJuros, 
                  descricaoJuros 
                });
                
                // Definir tipo de juros UI corretamente
                if (descricaoJuros.toUpperCase().includes('SEM') || valorJuros === 0) {
                  setTipoJurosUI('SEM_JUROS');
                } else if (tipoJuros.toUpperCase() === 'PERCENTUAL') {
                  setTipoJurosUI('PERCENTUAL');
                } else if (tipoJuros.toUpperCase() === 'ABSOLUTO') {
                  setTipoJurosUI('ABSOLUTO');
                } else {
                  setTipoJurosUI('');
                }
              } else {
                console.warn('⚠️ Configuração não encontrada para ID:', configId);
              }
              
              // Preencher outros campos automaticamente
              if (vendaData.quantidadeParcelas || vendaData.QuantidadeParcelas) {
                setQuantidadeParcelas(vendaData.quantidadeParcelas || vendaData.QuantidadeParcelas);
              }
              
              if (vendaData.dataInicioVencimento || vendaData.DataInicioVencimento) {
                const dataVencimento = vendaData.dataInicioVencimento || vendaData.DataInicioVencimento;
                setDataInicioVencimento(new Date(dataVencimento).toISOString().split('T')[0]);
              }
              
              // Aguardar um pouco para garantir que os states foram atualizados, então executar cálculo automaticamente
              setTimeout(() => {
                console.log('🔄 Forçando cálculo automático de pagamento após preenchimento...');
                
                // Calcular valor original baseado na venda (valorOrc - desconto)
                const valorOrc = vendaData.valorOrc || vendaData.ValorOrc || 0;
                const desconto = vendaData.desconto || vendaData.Desconto || 0;
                const valorOriginalBase = valorOrc - desconto;
                
                console.log('💰 Valor orçamento:', valorOrc);
                console.log('💰 Desconto:', desconto);
                console.log('💰 Valor original base:', valorOriginalBase);
                console.log('🧩 Configuração selecionada ID:', configId);
                
                if (configId && valorOriginalBase > 0) {
                  executarCalculoPagamento(valorOriginalBase);
                } else {
                  console.warn('⚠️ Não foi possível executar cálculo automático - dados insuficientes');
                }
              }, 200); // Timeout reduzido para execução mais rápida
            }
          }, 500);
        }
        
      } catch (error) {
        console.error('❌ Erro ao buscar dados da venda:', error);
        // Mesmo com erro, ainda abre o popup
      }
    }
  };

  // Função para fechar popup
  const fecharPopup = () => {
    setPopupAberto(false);
    setContaSelecionada(null);
    // Limpar configurações de pagamento
    setTipoPagamentoSelecionado(null);
    setConfiguracaoSelecionadaId(null);
    setQuantidadeParcelas(1);
    setDataInicioVencimento(new Date().toISOString().split('T')[0]);
    setCalculoPagamento(null);
    setErroCalculo('');
    setTipoJurosUI('');
  };

  // Carregar configurações de pagamento
  const fetchConfiguracoesPagamento = async () => {
    try {
      const data = await apiCall('ConfiguracaoPagamento');
      console.log('🧩 Configurações de pagamento carregadas:', data);
      setConfiguracoesPagamento(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('❌ Erro ao carregar configurações:', e);
      setConfiguracoesPagamento([]);
    }
  };

  // Filtrar configurações por tipo de pagamento
  const filtrarConfiguracoesPorTipo = (tipoPagamentoId) => {
    if (!tipoPagamentoId) {
      setConfiguracoesFiltradas([]);
      return;
    }
    const filtradas = configuracoesPagamento.filter(cfg => 
      (cfg.tipoPagamentoId || cfg.TipoPagamentoId) === tipoPagamentoId && 
      (cfg.ativo === undefined || cfg.ativo === true || cfg.Ativo === undefined || cfg.Ativo === true)
    );
    console.log(`🔍 Filtradas ${filtradas.length} configurações para tipo ${tipoPagamentoId}`);
    setConfiguracoesFiltradas(filtradas);
  };

  // Executar cálculo de pagamento
  const executarCalculoPagamento = async (valorBase) => {
    if (!configuracaoSelecionadaId) {
      setErroCalculo('');
      return;
    }

    setCarregandoCalculo(true);
    setErroCalculo('');

    try {
      const cfg = configuracoesPagamento.find(c => (c.id || c.Id) === configuracaoSelecionadaId);
      if (!cfg) throw new Error('Configuração não encontrada');

      const valorOriginalBase = valorBase;
      const modalidade = cfg.modalidadePagamentoDescricao || cfg.ModalidadePagamentoDescricao || '';
      const descricaoJuros = cfg.configuracaoJurosDescricao || cfg.ConfiguracaoJurosDescricao || '';
      const tipoJuros = cfg.tipoJuros || cfg.TipoJuros || '';
      const taxa = cfg.valorJuros || cfg.ValorJuros || 0;
      
      const isAVista = modalidade.includes('VISTA');
      const temJuros = descricaoJuros.includes('COM') && taxa > 0 && tipoJuros !== '';
      const qtd = Math.max(1, parseInt(quantidadeParcelas) || 1);

      let valorOriginal = valorOriginalBase;
      let valorComJuros = valorOriginal;
      let valorJuros = 0;
      let observacao = '';

      if (temJuros) {
        if (tipoJuros === 'ABSOLUTO') {
          valorJuros = taxa;
          valorComJuros = valorOriginal + valorJuros;
          observacao = `Juros absoluto R$ ${valorJuros.toFixed(2)}`;
        } else if (tipoJuros === 'PERCENTUAL') {
          if (!isAVista && qtd > 1) {
            const fator = 1 + (taxa / 100);
            valorComJuros = valorOriginal * Math.pow(fator, qtd);
            valorJuros = valorComJuros - valorOriginal;
            observacao = `Juros composto ${taxa}% por parcela (${qtd}x)`;
          } else {
            valorJuros = (valorOriginal * taxa) / 100;
            valorComJuros = valorOriginal + valorJuros;
            observacao = `Juros simples ${taxa}%`;
          }
        }
      } else {
        observacao = 'Sem juros';
      }

      const valorParcelaBruto = valorComJuros / qtd;
      const parcelas = [];
      for (let i = 1; i <= qtd; i++) {
        let valorParcela = valorParcelaBruto;
        if (i === qtd) {
          const somaAnteriores = valorParcelaBruto * (qtd - 1);
          valorParcela = valorComJuros - somaAnteriores;
        }
        const data = new Date(dataInicioVencimento || new Date().toISOString().split('T')[0]);
        data.setMonth(data.getMonth() + (i - 1));
        parcelas.push({
          numeroParcela: i,
          valorParcela: valorParcela,
          dataVencimento: data.toISOString(),
          observacoes: i === qtd ? 'Parcela final (ajuste)' : null
        });
      }

      const simulacao = {
        valorOriginal,
        valorJuros,
        valorFinal: valorComJuros,
        quantidadeParcelas: qtd,
        valorParcela: valorParcelaBruto,
        parcelas,
        observacaoCalculo: observacao + ' (simulação local)'
      };

      setCalculoPagamento(simulacao);
    } catch (e) {
      setErroCalculo('Erro ao simular: ' + (e.message || 'desconhecido'));
    } finally {
      setCarregandoCalculo(false);
    }
  };

  return (
    <div className="container-geral fundo-branco" style={{ 
      background: '#f0f0f0', 
      padding: '20px'
    }}>
      {/* Extrato: tudo o que entrou (Stripe e pagamentos externos), abre numa nova aba */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => window.open('/extrato', '_blank')}
          style={{ padding: '12px 24px', backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 16, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
        >
          🧾 Extrato
        </button>
      </div>

      {/* Filtro de contas a receber */}
      <div className="contas-receber-filtro-container">
        <label className="contas-receber-filtro-label">
          Filtrar por:
        </label>
        <div className="contas-receber-filtro-controles">
          <div className="contas-receber-filtro-input-container">
            <input
              type="text"
              className="contas-receber-filtro-input"
              placeholder={`Filtrar por ${opcoesFiltro.find(opcao => opcao.valor === filtro)?.label || 'campo'}`}
              value={valorFiltro}
              onChange={(e) => setValorFiltro(e.target.value)}
            />
            {valorFiltro && (
              <button
                onClick={limparFiltro}
                title="Limpar filtro"
                className="contas-receber-filtro-limpar"
              >
                ×
              </button>
            )}
          </div>
          <div className="contas-receber-filtro-dropdown-container">
            <button
              onClick={() => setDropdownAberto(!dropdownAberto)}
              className="contas-receber-filtro-dropdown-btn"
            >
              {opcoesFiltro.find(opcao => opcao.valor === filtro)?.label || 'Selecione'}
              <span style={{ transform: dropdownAberto ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {dropdownAberto && (
              <div className="contas-receber-filtro-dropdown-menu">
                {opcoesFiltro.map(opcao => (
                  <div 
                    key={opcao.valor}
                    onClick={() => {
                      setFiltro(opcao.valor);
                      setDropdownAberto(false);
                    }}
                    className="contas-receber-filtro-dropdown-item"
                  >
                    {opcao.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barra de informações e refresh */}
      <div style={{
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto 20px auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 20px'
      }}>
        <div style={{ color: '#666', fontSize: '14px' }}>
          {loading ? 'Carregando...' : `${contasReceberFiltradas.length} conta(s) encontrada(s)`}
        </div>
        <button
          onClick={fetchContasReceber}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: loading ? '#ccc' : '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!loading) e.target.style.backgroundColor = '#1976d2';
          }}
          onMouseLeave={(e) => {
            if (!loading) e.target.style.backgroundColor = '#2196f3';
          }}
        >
          🔄 {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: 'white',
          marginTop: '10px'
        }}>
          Carregando contas a receber...
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          color: '#ff0000',
          backgroundColor: '#ffebee',
          borderRadius: '5px',
          margin: '20px 0'
        }}>
          {error}
        </div>
      )}

      {/* Sistema de Abas - Sempre visível */}
      <div style={{ 
        width: '100%', 
        maxWidth: 1200, 
        margin: '20px auto 0 auto',
        padding: '0 20px'
      }}>
        {/* Container das Abas */}
        <div style={{ 
          display: 'flex',
          marginBottom: '0'
        }}>
          {/* Aba Contas de Serviços */}
          <button
            onClick={() => setAbaAtiva('servicos')}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderBottom: abaAtiva === 'servicos' ? '3px solid rgb(235, 133, 0)' : '1px solid #ddd',
              background: abaAtiva === 'servicos' 
                ? 'white' 
                : '#f5f5f5',
              color: abaAtiva === 'servicos' ? 'rgb(235, 133, 0)' : '#666',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              borderBottomLeftRadius: '0',
              borderBottomRightRadius: '0',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'none',
              marginRight: '0',
              position: 'relative',
              zIndex: abaAtiva === 'servicos' ? 2 : 1,
              marginBottom: '-1px',
              boxShadow: abaAtiva === 'servicos' 
                ? 'inset 0 -3px 6px rgba(235, 133, 0, 0.2), 0 5px 15px rgba(235, 133, 0, 0.7)' 
                : 'none'
            }}
          >
            Contas de Serviços
          </button>

          {/* Aba Contas de Produtos */}
          <button
            onClick={() => setAbaAtiva('produtos')}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderBottom: abaAtiva === 'produtos' ? '3px solid rgb(235, 133, 0)' : '1px solid #ddd',
              background: abaAtiva === 'produtos' 
                ? 'white' 
                : '#f5f5f5',
              color: abaAtiva === 'produtos' ? 'rgb(235, 133, 0)' : '#666',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              borderBottomLeftRadius: '0',
              borderBottomRightRadius: '0',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'none',
              position: 'relative',
              zIndex: abaAtiva === 'produtos' ? 2 : 1,
              marginBottom: '-1px',
              boxShadow: abaAtiva === 'produtos' 
                ? 'inset 0 -3px 6px rgba(235, 133, 0, 0.2), 0 5px 15px rgba(235, 133, 0, 0.7)' 
                : 'none'
            }}
          >
            Contas de Produtos
          </button>
        </div>

        {/* Container do Conteúdo das Abas */}
        <div style={{
          backgroundColor: 'white',
          border: 'none',
          borderRadius: '0 8px 8px 8px',
          minHeight: '400px',
          padding: '20px',
          borderTop: '1px solid #ddd'
        }}>
          {abaAtiva === 'servicos' && (
            <div>
              {loadingContas ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  Carregando contas a receber de serviços...
                </div>
              ) : errorContas ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#d32f2f' }}>
                  {errorContas}
                </div>
              ) : contasDeServicos.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  Nenhuma conta de serviço encontrada
                </div>
              ) : (
                <div className="contas-receber-cards-container">
                  {contasDeServicos.map((conta, index) => (
                    <div key={conta.id || index} className="contas-receber-card" style={{borderLeft: '2.5px solid #ff6600'}}>
                      <div className="contas-receber-card-content">
                        <div className="contas-receber-card-header">
                          <h3 className="contas-receber-card-title">
                            Conta a Receber #{conta.id}
                          </h3>
                          <div className="contas-receber-status-badge" style={{backgroundColor: getStatusColor(conta.statusPagamento)}}>
                            {conta.statusPagamento || 'Pendente'}
                          </div>
                        </div>

                        <div className="contas-receber-card-fields">
                          <div className="contas-receber-field contas-receber-field-id">
                            <label className="contas-receber-field-label">ID:</label>
                            <span className="contas-receber-field-value">{conta.id}</span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-cliente">
                            <label className="contas-receber-field-label">Cliente:</label>
                            <span className="contas-receber-field-value">{conta.nomeCliente || `ID: ${conta.clienteId}`}</span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-valor">
                            <label className="contas-receber-field-label">Valor Total (R$):</label>
                            <span className="contas-receber-field-value contas-receber-valor-destaque">{formatarMoeda(conta.valorTotal)}</span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-vencimento">
                            <label className="contas-receber-field-label">Data de Vencimento:</label>
                            <span className="contas-receber-field-value">{formatarData(conta.dataVencimento)}</span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-emissao">
                            <label className="contas-receber-field-label">Data de Emissão:</label>
                            <span className="contas-receber-field-value">{formatarData(conta.dataEmissao)}</span>
                          </div>
                          
                          {conta.vendaId && (
                            <div className="contas-receber-field contas-receber-field-venda">
                              <label className="contas-receber-field-label">Venda:</label>
                              <span className="contas-receber-field-value">#{conta.vendaId}</span>
                            </div>
                          )}
                          
                          {conta.orcamentoId && (
                            <div className="contas-receber-field contas-receber-field-orcamento">
                              <label className="contas-receber-field-label">Orçamento:</label>
                              <span className="contas-receber-field-value">#{conta.orcamentoId}</span>
                            </div>
                          )}
                          
                          <div className="contas-receber-field contas-receber-field-parcelas">
                            <label className="contas-receber-field-label">Parcelas:</label>
                            <span className="contas-receber-field-value">
                              {conta.parcelasPagas || 0}/{conta.qdeParcelasTotal || 1}
                            </span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-juros">
                            <label className="contas-receber-field-label">Juros:</label>
                            <span className="contas-receber-field-value">
                              {(() => {
                                const infoJuros = obterInfoJuros(conta);
                                if (infoJuros.valor > 0) {
                                  return `${formatarMoeda(infoJuros.valor)} (${infoJuros.tipo})`;
                                } else {
                                  return 'Sem juros';
                                }
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="contas-receber-card-actions">
                        <button 
                          className="btn-abrir-conta"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirPopupConta(conta);
                          }}>
                          Abrir Conta
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {abaAtiva === 'produtos' && (
            <div>
              {loadingContas ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  Carregando contas a receber de produtos...
                </div>
              ) : errorContas ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#d32f2f' }}>
                  {errorContas}
                </div>
              ) : contasDeProdutosFiltradas.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  Nenhuma conta de produto encontrada
                </div>
              ) : (
                <div className="contas-receber-cards-container">
                  {contasDeProdutosFiltradas.map((conta, index) => (
                    <div key={conta.id || index} className="contas-receber-card" style={{borderLeft: '2.5px solid #ff6600'}}>
                      <div className="contas-receber-card-content">
                        <div className="contas-receber-card-header">
                          <h3 className="contas-receber-card-title">
                            Conta de Produto #{conta.id}
                          </h3>
                          <div className="contas-receber-status-badge" style={{backgroundColor: getStatusColor(conta.statusPagamento)}}>
                            {conta.statusPagamento || 'Pendente'}
                          </div>
                        </div>

                        <div className="contas-receber-card-fields">
                          <div className="contas-receber-field contas-receber-field-id">
                            <label className="contas-receber-field-label">ID:</label>
                            <span className="contas-receber-field-value">{conta.id}</span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-cliente">
                            <label className="contas-receber-field-label">Cliente:</label>
                            <span className="contas-receber-field-value">{conta.nomeCliente || `ID: ${conta.clienteId}`}</span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-valor">
                            <label className="contas-receber-field-label">Valor Total (R$):</label>
                            <span className="contas-receber-field-value contas-receber-valor-destaque">{formatarMoeda(conta.valorTotal)}</span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-vencimento">
                            <label className="contas-receber-field-label">Data de Vencimento:</label>
                            <span className="contas-receber-field-value">{formatarData(conta.dataVencimento)}</span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-emissao">
                            <label className="contas-receber-field-label">Data de Emissão:</label>
                            <span className="contas-receber-field-value">{formatarData(conta.dataEmissao)}</span>
                          </div>
                          
                          {conta.vendaId && (
                            <div className="contas-receber-field contas-receber-field-venda">
                              <label className="contas-receber-field-label">Venda:</label>
                              <span className="contas-receber-field-value">#{conta.vendaId}</span>
                            </div>
                          )}
                          
                          <div className="contas-receber-field contas-receber-field-parcelas">
                            <label className="contas-receber-field-label">Parcelas:</label>
                            <span className="contas-receber-field-value">
                              {conta.parcelasPagas || 0}/{conta.qdeParcelasTotal || 1}
                            </span>
                          </div>
                          
                          <div className="contas-receber-field contas-receber-field-juros">
                            <label className="contas-receber-field-label">Juros:</label>
                            <span className="contas-receber-field-value">
                              {(() => {
                                const infoJuros = obterInfoJuros(conta);
                                if (infoJuros.valor > 0) {
                                  return `${formatarMoeda(infoJuros.valor)} (${infoJuros.tipo})`;
                                } else {
                                  return 'Sem juros';
                                }
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="contas-receber-card-actions">
                        <button 
                          className="btn-abrir-conta"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirPopupConta(conta);
                          }}>
                          Abrir Conta
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Popup de Detalhes da Conta */}
      {popupAberto && contaSelecionada && (
        <div className="contas-receber-popup-overlay">
          <div className="contas-receber-popup-container">
            {/* Header do Popup */}
            <div className="contas-receber-popup-header">
              <h2 className="contas-receber-popup-title">
                {contaSelecionada.orcamentoId ? 
                  `Detalhes da Conta a Receber #${contaSelecionada.id}` : 
                  `Detalhes da Conta de Produto #${contaSelecionada.id}`
                }
              </h2>
              <button
                onClick={fecharPopup}
                className="contas-receber-popup-close"
              >
                ×
              </button>
            </div>

            {/* Conteúdo do Card da Conta (mesmo card sem o botão) */}
            <div className="contas-receber-popup-content">
              <div className="contas-receber-popup-card">
                {/* Container dos Dados */}
                <div className="contas-receber-popup-card-body">
                  {/* Header com ID da conta e status */}
                  <div className="contas-receber-popup-card-header">
                    <h3 className="contas-receber-popup-card-title">
                      Conta a Receber #{contaSelecionada.id}
                    </h3>
                    <div className="contas-receber-status-badge" style={{backgroundColor: getStatusColor(contaSelecionada.statusPagamento)}}>
                      {contaSelecionada.statusPagamento || 'Pendente'}
                    </div>
                  </div>

                  {/* Grid de informações principais */}
                  <div className="contas-receber-popup-info-grid">
                    <div className="contas-receber-popup-field">
                      <label className="contas-receber-popup-field-label">
                        Cliente
                      </label>
                      <span className="contas-receber-popup-field-value">
                        {contaSelecionada.clienteNome || 'Cliente não informado'}
                      </span>
                    </div>
                    
                    <div className="contas-receber-popup-field">
                      <label className="contas-receber-popup-field-label">
                        Valor Total (R$)
                      </label>
                      <span className="contas-receber-popup-field-value contas-receber-valor-destaque">
                        {formatarMoeda(contaSelecionada.valorTotal)}
                      </span>
                    </div>
                    
                    <div className="contas-receber-popup-field">
                      <label className="contas-receber-popup-field-label">
                        Vencimento
                      </label>
                      <span className="contas-receber-popup-field-value" style={{
                        color: new Date(contaSelecionada.dataVencimento) < new Date() ? '#d32f2f' : '#333'
                      }}>
                        {formatarData(contaSelecionada.dataVencimento)}
                      </span>
                    </div>
                    
                    <div className="contas-receber-popup-field">
                      <label className="contas-receber-popup-field-label">
                        Data de Emissão
                      </label>
                      <span className="contas-receber-popup-field-value">
                        {formatarData(contaSelecionada.dataEmissao)}
                      </span>
                    </div>
                  </div>

                  {/* Informações adicionais em grid */}
                  <div className="contas-receber-popup-info-adicional">
                    {contaSelecionada.vendaId && (
                      <div>
                        <span style={{ color: '#666', marginRight: '8px' }}>Venda:</span>
                        <span style={{ fontWeight: '600', color: '#1976d2' }}>#{contaSelecionada.vendaId}</span>
                      </div>
                    )}
                    
                    {contaSelecionada.orcamentoId && (
                      <div>
                        <span style={{ color: '#666', marginRight: '8px' }}>Orçamento:</span>
                        <span style={{ fontWeight: '600', color: '#1976d2' }}>#{contaSelecionada.orcamentoId}</span>
                      </div>
                    )}
                    
                    {/* Mostrar tipo baseado no que tem */}
                    {!contaSelecionada.orcamentoId && contaSelecionada.vendaId && (
                      <div>
                        <span style={{ color: '#666', marginRight: '8px' }}>Tipo:</span>
                        <span style={{ fontWeight: '600', color: '#9c27b0' }}>Produto</span>
                      </div>
                    )}
                    
                    {(contaSelecionada.qdeParcelasTotal && contaSelecionada.qdeParcelasTotal > 1) && (
                      <div>
                        <span style={{ color: '#666', marginRight: '8px' }}>Parcelas:</span>
                        <span style={{ fontWeight: '600', color: '#333' }}>
                          {contaSelecionada.parcelasPagas || 0}/{contaSelecionada.qdeParcelasTotal}
                        </span>
                      </div>
                    )}
                    
                    {contaSelecionada.juros && contaSelecionada.juros > 0 && (
                      <div>
                        <span style={{ color: '#666', marginRight: '8px' }}>Juros:</span>
                        <span style={{ fontWeight: '600', color: 'rgb(208, 116, 0)' }}>
                          {formatarMoeda(contaSelecionada.juros)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Seção de Configuração de Pagamento */}
              {tipoPagamentoSelecionado && (
                <div className="contas-receber-config-pagamento">
                  <h4 style={{marginTop:0, color:'#333'}}>Configuração de Pagamento</h4>
                  {configuracoesFiltradas.length === 0 ? (
                    <div style={{color:'#a00', fontSize:'14px'}}>Nenhuma configuração ativa para este tipo de pagamento.</div>
                  ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'25px' }}>
                      <div>
                        <label style={{display:'block', fontWeight:500, marginBottom:4}}>Configuração</label>
                        <select
                          value={configuracaoSelecionadaId || ''}
                          onChange={(e) => {
                            const id = parseInt(e.target.value) || null;
                            setConfiguracaoSelecionadaId(id);
                            setCalculoPagamento(null);
                            setErroCalculo('');
                            const cfg = configuracoesPagamento.find(c => (c.id || c.Id) === id);
                            if (cfg) {
                              const maxParcelas = cfg.maximoParcelas || cfg.MaximoParcelas || 1;
                              setQuantidadeParcelas(Math.min(quantidadeParcelas, maxParcelas));
                              setTipoJurosUI(cfg.tipoJuros || cfg.TipoJuros || '');
                              
                              // Re-executar cálculo automaticamente quando configuração é selecionada
                              if (contaSelecionada) {
                                setTimeout(() => {
                                  executarCalculoPagamento(contaSelecionada.valorTotal);
                                }, 300);
                              }
                            }
                          }}
                          disabled={true}
                          style={{width:'100%', padding:'8px', border:'1px solid #ddd', borderRadius:'4px', background:'#f5f5f5', color:'#666'}}
                        >
                          <option value=''>Selecione</option>
                          {configuracoesFiltradas.map(cfg => {
                            const label = (cfg.descricaoCompleta || cfg.DescricaoCompleta) || 
                              [
                                cfg.tipoPagamentoDescricao || cfg.TipoPagamentoDescricao,
                                cfg.modalidadePagamentoDescricao || cfg.ModalidadePagamentoDescricao,
                                cfg.configuracaoJurosDescricao || cfg.ConfiguracaoJurosDescricao,
                                cfg.tipoJuros || cfg.TipoJuros ? `(${(cfg.tipoJuros||cfg.TipoJuros)}${cfg.valorJuros||cfg.ValorJuros ? ':'+ (cfg.valorJuros||cfg.ValorJuros) : ''})` : null
                              ].filter(Boolean).join(' ');
                            return (
                              <option key={cfg.id || cfg.Id} value={cfg.id || cfg.Id}>
                                {label.trim() || `Configuração #${cfg.id || cfg.Id}`}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      {configuracaoSelecionadaId && (() => {
                        const cfg = configuracoesPagamento.find(c => (c.id || c.Id) === configuracaoSelecionadaId);
                        if (!cfg) return null;
                        const maxParcelas = cfg.maximoParcelas || cfg.MaximoParcelas || 1;
                        const descricaoJuros = cfg.configuracaoJurosDescricao || cfg.ConfiguracaoJurosDescricao;
                        const temJuros = (descricaoJuros||'').toUpperCase().includes('COM');
                        const modalidade = cfg.modalidadePagamentoDescricao || cfg.ModalidadePagamentoDescricao || '';
                        const isAVista = modalidade.toUpperCase().includes('VISTA');
                        return (
                          <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                            <div style={{fontSize:'13px', color:'#444'}}>
                              <strong>Modalidade:</strong> {modalidade || '—'}<br/>
                              <strong>Juros:</strong> {descricaoJuros || '—'} {temJuros && (<>
                                | Tipo: {cfg.tipoJuros || cfg.TipoJuros || '—'}
                                {cfg.valorJuros ? ` | Valor: ${(cfg.tipoJuros||cfg.TipoJuros||'').toUpperCase()==='PERCENTUAL' ? (cfg.valorJuros || cfg.ValorJuros)+'%' : 'R$ '+(cfg.valorJuros||cfg.ValorJuros)}` : ''}
                              </>)}<br/>
                              <strong>Máx Parcelas:</strong> {maxParcelas}<br/>
                              {cfg.valorMinimoParcela || cfg.ValorMinimoParcela ? <><strong>Parcela Mínima:</strong> R$ {(cfg.valorMinimoParcela || cfg.ValorMinimoParcela).toFixed ? (cfg.valorMinimoParcela || cfg.ValorMinimoParcela).toFixed(2) : (cfg.valorMinimoParcela || cfg.ValorMinimoParcela)}</> : null}
                            </div>
                            {!isAVista && (
                              <div style={{display:'flex', gap:'15px'}}>
                                <div>
                                  <label style={{display:'block', fontSize:13, marginBottom:4}}>Qtd Parcelas</label>
                                  <input type='number' min={1} max={maxParcelas}
                                    value={quantidadeParcelas}
                                    onChange={e=> {
                                      setQuantidadeParcelas(Math.min(parseInt(e.target.value)||1, maxParcelas));
                                      // Re-executar cálculo quando quantidade de parcelas muda
                                      if (configuracaoSelecionadaId && contaSelecionada) {
                                        setTimeout(() => {
                                          executarCalculoPagamento(contaSelecionada.valorTotal);
                                        }, 300);
                                      }
                                    }}
                                    readOnly={true}
                                    style={{padding:'6px', border:'1px solid #ccc', borderRadius:4, width:'120px', background:'#f5f5f5', color:'#666'}} />
                                </div>
                                <div>
                                  <label style={{display:'block', fontSize:13, marginBottom:4}}>1º Vencimento</label>
                                  <input type='date'
                                    value={dataInicioVencimento}
                                    onChange={e=> {
                                      setDataInicioVencimento(e.target.value);
                                      // Re-executar cálculo quando data de vencimento muda
                                      if (configuracaoSelecionadaId && contaSelecionada) {
                                        setTimeout(() => {
                                          executarCalculoPagamento(contaSelecionada.valorTotal);
                                        }, 300);
                                      }
                                    }}
                                    readOnly={true}
                                    style={{padding:'6px', border:'1px solid #ccc', borderRadius:4, background:'#f5f5f5', color:'#666'}} />
                                </div>
                              </div>
                            )}
                            {/* Inputs manuais de Juros */}
                            {temJuros && (
                              <div style={{display:'flex', gap:'15px', flexWrap:'wrap'}}>
                                <div>
                                  <label style={{display:'block', fontSize:13, marginBottom:4}}>Tipo de Juros</label>
                                  <select
                                    value={tipoJurosUI}
                                    onChange={e => {
                                      const val = e.target.value;
                                      setTipoJurosUI(val);
                                      if (val === 'SEM_JUROS') {
                                        cfg.tipoJuros = 'ABSOLUTO';
                                        cfg.valorJuros = 0;
                                      } else {
                                        cfg.tipoJuros = val;
                                      }
                                      setConfiguracoesPagamento([...configuracoesPagamento]);
                                      
                                      // Re-executar cálculo quando tipo de juros muda
                                      if (configuracaoSelecionadaId && contaSelecionada) {
                                        setTimeout(() => {
                                          executarCalculoPagamento(contaSelecionada.valorTotal);
                                        }, 300);
                                      }
                                    }}
                                    disabled={true}
                                    style={{padding:'6px', border:'1px solid #ccc', borderRadius:4, background:'#f5f5f5', color:'#666'}}
                                  >
                                    <option value=''>Selecione</option>
                                    <option value='SEM_JUROS'>Sem Juros</option>
                                    <option value='ABSOLUTO'>Absoluto (R$)</option>
                                    <option value='PERCENTUAL'>Percentual (%)</option>
                                  </select>
                                </div>
                                <div>
                                  <label style={{display:'block', fontSize:13, marginBottom:4}}>Valor Juros (R$)</label>
                                  <input
                                    type='number'
                                    step='0.01'
                                    min='0'
                                    value={tipoJurosUI === 'SEM_JUROS' ? 0 : ((cfg.valorJuros || cfg.ValorJuros) ?? '')}
                                    onChange={e => {
                                      if (tipoJurosUI === 'SEM_JUROS') return;
                                      const v = parseFloat(e.target.value);
                                      if (!isNaN(v)) cfg.valorJuros = v; else cfg.valorJuros = null;
                                      setConfiguracoesPagamento([...configuracoesPagamento]);
                                      
                                      // Re-executar cálculo quando valor de juros muda
                                      if (configuracaoSelecionadaId && contaSelecionada) {
                                        setTimeout(() => {
                                          executarCalculoPagamento(contaSelecionada.valorTotal);
                                        }, 300);
                                      }
                                    }}
                                    readOnly={true}
                                    placeholder="R$ 0,00"
                                    style={{padding:'6px', border:'1px solid #ccc', borderRadius:4, width:'120px', background:'#f5f5f5', color:'#666'}}
                                  />
                                </div>
                                <div style={{alignSelf:'flex-end', fontSize:12, color:'#555'}}>
                                  {tipoJurosUI === 'SEM_JUROS' ? '(Sem juros aplicados)' : '(Ajuste local – cálculo backend só após Aprovar)'}
                                </div>
                              </div>
                            )}
                            {erroCalculo && <div style={{color:'#b00020', fontSize:'13px'}}>{erroCalculo}</div>}
                            
                            {/* Resultado do Cálculo - sempre visível */}
                            <div style={{marginTop:'10px', fontSize:'13px', background:'#fff', padding:'12px', border:'1px solid #e0e0e0', borderRadius:6}}>
                              <strong>Resultado do Cálculo:</strong><br/>
                              {carregandoCalculo ? (
                                <div style={{color:'#666', fontStyle:'italic'}}>Calculando...</div>
                              ) : calculoPagamento ? (
                                <>
                                  Valor Original: R$ {calculoPagamento.valorOriginal?.toFixed(2)}<br/>
                                  Juros: R$ {calculoPagamento.valorJuros?.toFixed(2)}<br/>
                                  Valor Final: <span style={{color:'#0a7d24', fontWeight:600}}>R$ {calculoPagamento.valorFinal?.toFixed(2)}</span><br/>
                                  Parcelas: {calculoPagamento.quantidadeParcelas}x de R$ {calculoPagamento.valorParcela?.toFixed(2)}<br/>
                                  {calculoPagamento.observacaoCalculo && <em>{calculoPagamento.observacaoCalculo}</em>}
                                  {calculoPagamento.parcelas && calculoPagamento.parcelas.length > 0 && (
                                    <table style={{width:'100%', marginTop:'10px', borderCollapse:'collapse', fontSize:'12px'}}>
                                      <thead>
                                        <tr style={{background:'#f0f0f0'}}>
                                          <th style={{border:'1px solid #ddd', padding:4}}>Parcela</th>
                                          <th style={{border:'1px solid #ddd', padding:4}}>Valor</th>
                                          <th style={{border:'1px solid #ddd', padding:4}}>Vencimento</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {calculoPagamento.parcelas.map(p => (
                                          <tr key={p.numeroParcela}>
                                            <td style={{border:'1px solid #eee', padding:4}}>{p.numeroParcela}</td>
                                            <td style={{border:'1px solid #eee', padding:4}}>R$ {p.valorParcela?.toFixed(2)}</td>
                                            <td style={{border:'1px solid #eee', padding:4}}>{new Date(p.dataVencimento).toLocaleDateString()}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </>
                              ) : (
                                <div style={{color:'#666', fontStyle:'italic'}}>
                                  {configuracaoSelecionadaId ? 'Aguardando cálculo...' : ''}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
